import React, { useEffect, useState } from "react";
import axios from "axios";
import CustomButton from "../pages/CustomButton";
import { getAuthHeaders } from "../services/useAuth";
import { BASE_URL } from "../config";
import { useNavigate } from "react-router-dom";

export default function BroadcastTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [divisionsOptions, setDivisionsOptions] = useState([]);
  const [statesOptions, setStatesOptions] = useState([]);
  const [designationsOptions, setDesignationsOptions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    async function init() {
      setLoading(true);
      try {
        const [divResp, statesResp, desigResp, broadcastsResp] =
          await Promise.allSettled([
            axios.get(`${BASE_URL}/api/divisions`, {
              headers: getAuthHeaders(),
            }),
            axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
            axios.get(`${BASE_URL}/api/designations/`, {
              headers: getAuthHeaders(),
            }),
            axios.get(`${BASE_URL}/api/broadcasts`, {
              headers: getAuthHeaders(),
            }),
          ]);

        const normalizeOptions = (r, fallback = []) => {
          if (!r || r.status !== "fulfilled" || !r.value || !r.value.data)
            return fallback;
          const arr = Array.isArray(r.value.data)
            ? r.value.data
            : r.value.data.results ?? [];
          return arr.map((item) => {
            const id =
              typeof item.id === "string" ? parseInt(item.id, 10) : item.id;
            const name =
              item.name ?? item.title ?? item.label ?? String(item.id);
            return { id: Number.isFinite(id) ? id : item.id, name };
          });
        };

        const divs = normalizeOptions(divResp, []);
        const states = normalizeOptions(statesResp, []);
        const desigs = normalizeOptions(desigResp, []);

        if (!mounted) return;
        setDivisionsOptions(divs);
        setStatesOptions(states);
        setDesignationsOptions(desigs);

        let broadcastsData = [];
        if (
          broadcastsResp?.status === "fulfilled" &&
          broadcastsResp.value?.data
        ) {
          const payload = Array.isArray(broadcastsResp.value.data)
            ? broadcastsResp.value.data
            : broadcastsResp.value.data.results ?? [];
          broadcastsData = payload.map(mapApiToRow(divs, states, desigs));
        }
        setRows(broadcastsData);
      } catch (err) {
        console.error("Init fetch error", err);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    init();
    return () => {
      mounted = false;
    };
  }, []);

  // Fixed mapApiToRow: no variable shadowing; supports both id-arrays and name-arrays from API
  function mapApiToRow(divs, statesOpts, desigs) {
    return function (apiObj) {
      const parseIds = (arr) =>
        Array.isArray(arr)
          ? arr.map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n))
          : [];

      const divisionIds = parseIds(apiObj.divisions);
      const stateIds = parseIds(apiObj.states);
      const designationIds = parseIds(apiObj.designations);

      const divisions =
        Array.isArray(apiObj.divisions) &&
        apiObj.divisions.length > 0 &&
        typeof apiObj.divisions[0] === "string"
          ? apiObj.divisions
          : divisionIds
              .map(
                (did) =>
                  (divs.find((x) => Number(x.id) === Number(did)) || {}).name
              )
              .filter(Boolean);

      const states =
        Array.isArray(apiObj.states) &&
        apiObj.states.length > 0 &&
        typeof apiObj.states[0] === "string"
          ? apiObj.states
          : stateIds
              .map(
                (sid) =>
                  (statesOpts.find((x) => Number(x.id) === Number(sid)) || {})
                    .name
              )
              .filter(Boolean);

      const designations =
        Array.isArray(apiObj.designations) &&
        apiObj.designations.length > 0 &&
        typeof apiObj.designations[0] === "string"
          ? apiObj.designations
          : designationIds
              .map(
                (desId) =>
                  (desigs.find((x) => Number(x.id) === Number(desId)) || {})
                    .name
              )
              .filter(Boolean);

      return {
        id: apiObj.id,
        message: apiObj.message ?? "",
        startDate: apiObj.start_date ?? "",
        endDate: apiObj.end_date ?? "",
        divisions,
        states,
        designations,
        active: apiObj.active === undefined ? true : !!apiObj.active,
        editing: false,
        isNew: false,
      };
    };
  }

  // Keep mapping to API same as before: convert names -> ids using options arrays
  function mapRowToApiPayload(row) {
    const divisions = (row.divisions || [])
      .map((name) => divisionsOptions.find((d) => d.name === name))
      .filter(Boolean)
      .map((d) => Number(d.id));

    const states = (row.states || [])
      .map((name) => statesOptions.find((s) => s.name === name))
      .filter(Boolean)
      .map((s) => Number(s.id));

    const designations = (row.designations || [])
      .map((name) => designationsOptions.find((d) => d.name === name))
      .filter(Boolean)
      .map((d) => Number(d.id));

    return {
      message: row.message,
      start_date: row.startDate,
      end_date: row.endDate,
      divisions,
      states,
      designations,
      active: !!row.active,
    };
  }

  function handleChange(id, key, value) {
    setRows((r) =>
      r.map((row) => (row.id === id ? { ...row, [key]: value } : row))
    );
  }
  function toggleEdit(id) {
    setRows((r) =>
      r.map((row) => (row.id === id ? { ...row, editing: !row.editing } : row))
    );
  }

  // Keeps row arrays as names (strings) to remain compatible with existing mapping
  function toggleCheckboxByName(rowId, key, name) {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== rowId) return row;
        const existing = Array.isArray(row[key]) ? [...row[key]] : [];
        const idx = existing.indexOf(name);
        if (idx === -1) existing.push(name);
        else existing.splice(idx, 1);
        return { ...row, [key]: existing };
      })
    );
  }

  function handleMultiSelect(id, key, event) {
    const options = Array.from(event.target.options)
      .filter((o) => o.selected)
      .map((o) => o.value);
    handleChange(id, key, options);
  }
  function addNewBroadcast() {
    navigate("/admin/broadcast-message");
  }

  async function handleUpdate(id) {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    const payload = mapRowToApiPayload(row);

    try {
      if (row.isNew) {
        const resp = await axios.post(`${BASE_URL}/api/broadcasts`, payload, {
          headers: getAuthHeaders(),
        });
        const created = mapApiToRow(
          divisionsOptions,
          statesOptions,
          designationsOptions
        )(resp.data);
        setRows((r) => r.map((rr) => (rr.id === id ? created : rr)));
      } else {
        await axios.put(`${BASE_URL}/api/broadcasts/${id}`, payload, {
          headers: getAuthHeaders(),
        });
        setRows((r) =>
          r.map((rr) => (rr.id === id ? { ...rr, editing: false } : rr))
        );
      }
    } catch (err) {
      console.error("Save failed", err);
    }
  }

  function handleCancel(id) {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    if (row.isNew) {
      setRows((r) => r.filter((rr) => rr.id !== id));
      return;
    }
    setRows((r) =>
      r.map((rr) => (rr.id === id ? { ...rr, editing: false } : rr))
    );
  }

  async function handleDelete(id) {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    if (row.isNew) {
      setRows((r) => r.filter((rr) => rr.id !== id));
      return;
    }
    try {
      await axios.delete(`${BASE_URL}/api/broadcasts/${id}`, {
        headers: getAuthHeaders(),
      });
      setRows((r) => r.filter((rr) => rr.id !== id));
    } catch (err) {
      console.error("Delete failed", err);
    }
  }

  // compute textarea rows (keeps dynamic height behavior)
  function computeRows(text) {
    if (!text) return 2;
    const lines = text
      .split("\n")
      .reduce((acc, l) => acc + Math.ceil(l.length / 50), 0);
    return Math.min(Math.max(lines, 2), 20);
  }

  // Checkbox list that uses option.name (keeps text size same as before)
  function CheckboxList({ row, keyName, options, editing }) {
    return (
      <div
        style={{ display: "flex", flexDirection: "column", gap: 6, padding: 4 }}
      >
        {options.map((opt) => {
          const checked =
            Array.isArray(row[keyName]) && row[keyName].includes(opt.name);
          return (
            <label
              key={opt.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: editing ? "pointer" : "default",
              }}
            >
              <input
                type="checkbox"
                checked={!!checked}
                disabled={!editing}
                onChange={() => toggleCheckboxByName(row.id, keyName, opt.name)}
                style={{ width: 16, height: 16 }}
              />
              <span className="text-sm" style={{ lineHeight: "1.1" }}>
                {opt.name}
              </span>
            </label>
          );
        })}
      </div>
    );
  }

  function renderRow(row, idx) {
    const today = new Date();
    const end = new Date(row.endDate);

    const isActivePeriod = end >= new Date(today.toDateString());

    // Apply FULL GREEN styling to entire row + text
    const rowClass = row.editing
      ? "bg-white"
      : isActivePeriod
      ? "bg-green-200 text-green-900 font-semibold"
      : "bg-pink-200 text-black";

    return (
      <tr key={row.id} className={`${rowClass} border-t`}>
        <td className="border-r text-center w-12 text-sm font-medium align-top py-2">
          {idx + 1}
        </td>

        <td className="border-r p-2 align-top w-72">
          <textarea
            className={`w-full border rounded text-sm leading-relaxed ${
              row.editing ? "bg-white" : "bg-transparent"
            }`}
            value={row.message}
            onChange={(e) => handleChange(row.id, "message", e.target.value)}
            disabled={!row.editing}
            rows={computeRows(row.message)}
            style={{ resize: "none", overflow: "visible" }}
          />
        </td>

        <td className="border-r p-2 align-top w-28 text-center">
          <input
            type="date"
            className={`w-full border rounded text-sm ${
              row.editing ? "bg-white" : "bg-transparent"
            }`}
            value={row.startDate}
            disabled={!row.editing}
            onChange={(e) => handleChange(row.id, "startDate", e.target.value)}
          />
        </td>

        <td className="border-r p-2 align-top w-28 text-center">
          <input
            type="date"
            className={`w-full border rounded text-sm ${
              row.editing ? "bg-white" : "bg-transparent"
            }`}
            value={row.endDate}
            disabled={!row.editing}
            onChange={(e) => handleChange(row.id, "endDate", e.target.value)}
          />
        </td>
        {/* Division checkbox list */}
        <td className="border-r p-2 align-top w-36 text-xs">
          <div style={{ maxHeight: `calc(100vh - 220px)`, overflow: "auto" }}>
            <CheckboxList
              row={row}
              keyName="divisions"
              options={divisionsOptions}
              editing={row.editing}
            />
          </div>
        </td>

        {/* State checkbox list */}
        <td className="border-r p-2 align-top w-44 text-xs">
          <div style={{ maxHeight: `calc(100vh - 220px)`, overflow: "auto" }}>
            <CheckboxList
              row={row}
              keyName="states"
              options={statesOptions}
              editing={row.editing}
            />
          </div>
        </td>

        {/* Designation checkbox list */}
        <td className="border-r p-2 align-top w-36 text-xs">
          <div style={{ maxHeight: "calc(100vh - 220px)", overflow: "auto" }}>
            <CheckboxList
              row={row}
              keyName="designations"
              options={designationsOptions}
              editing={row.editing}
            />
          </div>
        </td>

        <td className="border-r p-2 align-middle text-center w-20">
          <input
            type="checkbox"
            checked={!!row.active}
            onChange={(e) => handleChange(row.id, "active", e.target.checked)}
            disabled={!row.editing}
            className="transform scale-125"
          />
        </td>

        <td className="border align-top w-40 p-2">
          {row.editing ? (
            <div className="flex gap-2 items-center justify-center">
              <CustomButton
                onClick={() => handleUpdate(row.id)}
                variant="save"
                label="SAVE"
                className="px-3 py-1 text-sm"
              />
              <CustomButton
                onClick={() => handleCancel(row.id)}
                variant="cancel"
                label="CANCEL"
                className="px-3 py-1 text-sm"
              />
            </div>
          ) : (
            <div className="flex gap-2 items-center justify-center">
              <CustomButton
                onClick={() => toggleEdit(row.id)}
                variant="edit"
                label="EDIT"
                className="px-3 py-1 text-sm"
              />
              <CustomButton
                onClick={() => handleDelete(row.id)}
                variant="delete"
                label="Delete"
                className="px-3 py-1 text-sm"
              />
            </div>
          )}
        </td>
      </tr>
    );
  }

  return (
    <div className="bg-gray-50">
      <div className="bg-white w-full h-full">
        <div className="flex items-center justify-center">
          <button
            onClick={addNewBroadcast}
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-1 text-sm font-bold whitespace-nowrap  mx-auto max-w-xs p-2 border rounded h-10"
          >
            Add Broadcast
          </button>
        </div>
        <div className="p-2 text-center">
          <span className="font-semibold text-green-700">
            Green Color - Active Broadcast.
          </span>
          <span className="mx-2"> </span>
          <span className="font-semibold text-red-600">
            Red Color - DeActive Broadcast.
          </span>
        </div>

           <div className="border rounded shadow  table-wrapper">
        <table className="unlock-dcr-leave-table text-sm ">
          <thead className="bg-blue-500 text-white">
            <tr className="text-white bg-emerald-700">
              <th className="border p-2 w-12 text-sm">S.No</th>
              <th className="border p-2 w-72 text-sm text-left">Message</th>
              <th className="border p-2 w-28 text-sm">Start Date</th>
              <th className="border p-2 w-28 text-sm">End Date</th>
              <th className="border p-2 w-36 text-sm">Division</th>
              <th className="border p-2 w-44 text-sm">State</th>
              <th className="border p-2 w-36 text-sm">Designation</th>
              <th className="border p-2 w-20 text-sm">Active</th>
              <th className="border p-2 w-40 text-sm">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="p-6 text-center">
                  Loading...
                </td>
              </tr>
            ) : rows.length ? (
              rows.map((row, idx) => renderRow(row, idx))
            ) : (
              <tr>
                <td colSpan={9} className="p-6 text-center text-gray-500">
                  No broadcasts found. Click "Add Broadcast" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}
